import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useMyDevelopmentPlanApi, { GOAL_CATEGORIES, ACTION_TYPES, ACTION_STATUSES } from './config';
import IdpFlowBar from './IdpFlowBar';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import api from '../../services/api';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 760px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RevisionBanner = styled.div`
  padding: 10px 14px;
  background: ${({ theme }) => `${theme.colors.auxiliar.warning}1A`};
  border-left: 4px solid ${({ theme }) => theme.colors.auxiliar.warning};
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const GoalCard = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 14px;
  position: relative;
`;

const GoalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ theme }) => `${theme.colors.secondary.main}1A`};
  color: ${({ theme }) => theme.colors.secondary.dark};
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  font-size: 18px;
  padding: 2px 6px;
  line-height: 1;
`;

const ActionRow = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  position: relative;
`;

const ActionRowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const ProgressRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
`;

const ProgressLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 180px;
`;

const GoalGroupLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 12px 0 6px;
`;

const ReadyBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ theme }) => `${theme.colors.auxiliar.success}1A`};
  color: ${({ theme }) => theme.colors.auxiliar.success};
  margin-left: 8px;
`;

// Task 35 fix turu — MyGoalsPage'in TotalRow/GoalCount/SubmitHint deseniyle aynı.
const ValidationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  margin-bottom: 16px;
`;

const GoalCounterText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $valid, theme }) => ($valid ? theme.colors.text.muted : theme.colors.auxiliar.danger)};
`;

const ValidationHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const GoalActionWarning = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin: -4px 0 10px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 140px;
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.text.dark?.little || theme.colors.text.primary};
`;

// Her form satırı kalıcı benzersiz `_key` taşır (GoalEditor'ün emptyKr
// deseninin aynısı) — index-as-key, satır silinince yanlış satırın state'ini
// koruyordu. `_key` payload'a girmez (buildPlanPayload alan alan map'ler).
const newFormKey = () => Math.random().toString(36).slice(2);
const emptyAction = () => ({ _key: newFormKey(), actionTitle: '', actionType: 'course', targetDate: '', notes: '' });
// TD-30 — PLANIN TÜM AKSİYONLARI, TEK DÜZLEŞTİRME KURALI.
//
// Plan detayı aksiyonları yalnız `goals[].actions` altında gösteriyordu; hedefe bağlı
// OLMAYAN aksiyonlar (GoalId=null ya da hedefi pasifleşmiş öksüz satırlar) hiçbir ekranda
// görünmüyor ama ilerleme ortalamasını (backend IdpProgressRules.Rollup) AŞAĞI ÇEKİYORDU:
// çalışan "%100'e ne kaldı?" sorusunu cevaplayamıyor, o aksiyonu bulamıyor, ilerleme
// giremiyor, iptal edemiyordu — ReadyForReview kapısı da hiç açılmıyordu.
//
// Düzleştirme TEK yerde olduğu için detay listesi ile ilerleme modalı ayrışamaz.
// Saf + dışa açık: ekranı sürmeden test edilebilir (EmployeeReportsPage.buildFieldDefs deseni).
export const allActionsFlat = (plan, generalLabel) => [
  ...(plan?.goals || []).flatMap((g) => (g.actions || []).map((a) => ({ ...a, goalTitle: g.title }))),
  ...(plan?.generalActions || []).map((a) => ({ ...a, goalTitle: generalLabel })),
];

const emptyGoal = () => ({ _key: newFormKey(), title: '', category: 'technical', successCriteria: '', targetDate: '', actions: [emptyAction()] });
const emptyPlanForm = () => ({ title: '', description: '', careerGoal: '', cycleId: null, goals: [emptyGoal()] });

// Task 35 fix turu (İncelemeci Important #1) — backend SubmitAsync min1/max5 hedef +
// hedef-başına min1 aksiyon kuralını yalnız SUNUCUDA doğruluyordu; FE hangi hedefin
// eksik olduğunu göstermeden genel bir toast'a düşüyordu. `MyGoalsPage`'in
// sayaç+enoughGoals+ipucu deseniyle aynı: durum dizileri modül seviyesine taşındı ki
// hem `buildActions` hem render aynı listeyi kullansın.
const EDITABLE_STATUSES = ['draft', 'revision_required'];
const SUBMITTABLE_STATUSES = ['draft', 'revision_required'];
const CANCELABLE_STATUSES = ['draft', 'meeting', 'manager_approval', 'revision_required', 'active'];
const MIN_GOALS = 1;
const MAX_GOALS = 5;

function buildPlanPayload(planForm) {
  return {
    title: planForm.title,
    description: planForm.description || undefined,
    // P1 — kariyer hedefi (opsiyonel; UpdateMy tam-form PUT: boş bırakılırsa BE temizler).
    careerGoal: planForm.careerGoal || undefined,
    cycleId: planForm.cycleId || undefined,
    goals: planForm.goals
      .filter((g) => g.title.trim())
      .map((g) => ({
        title: g.title,
        category: g.category,
        successCriteria: g.successCriteria || undefined,
        targetDate: g.targetDate || undefined,
        actions: g.actions
          .filter((a) => a.actionTitle.trim())
          .map((a) => ({
            actionTitle: a.actionTitle,
            actionType: a.actionType,
            targetDate: a.targetDate || undefined,
            notes: a.notes || undefined,
          })),
      })),
  };
}

// Backend SubmitAsync'in "1-5 hedef + hedef başına >=1 aksiyon" kuralıyla BİREBİR
// AYNI — yalnız halihazırda dolu (title'ı olan) hedefler sayılır (taslakta boş satırlar
// serbest, tıpkı kaydetme sırasında olduğu gibi).
function computePlanValidation(goals) {
  const effectiveGoals = goals
    .filter((g) => g.title.trim())
    .map((g) => ({ title: g.title, validActionCount: g.actions.filter((a) => a.actionTitle.trim()).length }));
  const goalCount = effectiveGoals.length;
  const goalCountValid = goalCount >= MIN_GOALS && goalCount <= MAX_GOALS;
  const goalsMissingAction = effectiveGoals.filter((g) => g.validActionCount === 0).length;
  return { goalCount, goalCountValid, goalsMissingAction, submittable: goalCountValid && goalsMissingAction === 0 };
}

export default function MyDevelopmentPlansPage() {
  const { t, i18n } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [planModal, setPlanModal] = useState({ isOpen: false, plan: null });
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [savingPlan, setSavingPlan] = useState(false);
  const [submittingPlan, setSubmittingPlan] = useState(false);

  const [detailModal, setDetailModal] = useState({ isOpen: false, plan: null });
  const [detailLoading, setDetailLoading] = useState(false);

  const [progressModal, setProgressModal] = useState({ isOpen: false, plan: null });
  const [progressValues, setProgressValues] = useState({});
  const [progressBusy, setProgressBusy] = useState(false);

  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, planId: null, title: '' });
  const [cancelling, setCancelling] = useState(false);

  const {
    fetching, data, pagination, loadFailed, columns: baseColumns,
    create, update, getById, submit, cancel, updateActionProgress,
  } = useMyDevelopmentPlanApi(queryParams);

  const categoryOptions = useMemo(() => GOAL_CATEGORIES.map((v) => ({
    id: v, name: t(`performance.idp.category.${v}`),
  })), [t]);

  const actionTypeOptions = useMemo(() => ACTION_TYPES.map((v) => ({
    id: v, name: t(`performance.idp.actionType.${v}`),
  })), [t]);

  const actionStatusOptions = useMemo(() => ACTION_STATUSES.map((v) => ({
    id: v, name: t(`performance.idp.actionStatus.${v}`),
  })), [t]);

  // Task 35 fix turu (İncelemeci Important #1) — canlı hedef sayacı + Gönder disable
  // koşulu; backend SubmitAsync'in 1-5 hedef + hedef-başına min1 aksiyon kuralıyla
  // birebir aynı hesap.
  const planValidation = useMemo(() => computePlanValidation(planForm.goals), [planForm.goals]);

  // Task 38 cutover — eski /v1/performance-cycles söküldü; seçici artık PRD
  // EvaluationPeriod'a (/v1/evaluation-periods) bağlanır (aynı sözleşme: {id, name}).
  const cycleAsyncProps = useMemo(() => ({
    onSearch: async (q) => {
      const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/evaluation-periods', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((c) => ({ id: c.id, name: c.name }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/evaluation-periods/${id}`);
      const c = res.data?.data;
      return c ? { id: c.id, name: c.name } : null;
    },
  }), []);

  // ---- Goal/action helpers ----

  const addGoal = useCallback(() => {
    setPlanForm((prev) => ({ ...prev, goals: [...prev.goals, emptyGoal()] }));
  }, []);

  const removeGoal = useCallback((goalIdx) => {
    setPlanForm((prev) => {
      const next = [...prev.goals];
      next.splice(goalIdx, 1);
      return { ...prev, goals: next };
    });
  }, []);

  const updateGoalField = useCallback((goalIdx, field, value) => {
    setPlanForm((prev) => {
      const next = [...prev.goals];
      next[goalIdx] = { ...next[goalIdx], [field]: value };
      return { ...prev, goals: next };
    });
  }, []);

  const addAction = useCallback((goalIdx) => {
    setPlanForm((prev) => {
      const next = [...prev.goals];
      next[goalIdx] = { ...next[goalIdx], actions: [...next[goalIdx].actions, emptyAction()] };
      return { ...prev, goals: next };
    });
  }, []);

  const removeAction = useCallback((goalIdx, actionIdx) => {
    setPlanForm((prev) => {
      const next = [...prev.goals];
      const actions = [...next[goalIdx].actions];
      actions.splice(actionIdx, 1);
      next[goalIdx] = { ...next[goalIdx], actions };
      return { ...prev, goals: next };
    });
  }, []);

  const updateActionField = useCallback((goalIdx, actionIdx, field, value) => {
    setPlanForm((prev) => {
      const next = [...prev.goals];
      const actions = [...next[goalIdx].actions];
      actions[actionIdx] = { ...actions[actionIdx], [field]: value };
      next[goalIdx] = { ...next[goalIdx], actions };
      return { ...prev, goals: next };
    });
  }, []);

  // ---- Plan modal ----

  const openCreateModal = useCallback(() => {
    setPlanForm(emptyPlanForm());
    setPlanModal({ isOpen: true, plan: null });
  }, []);

  const openEditModal = useCallback(async (row) => {
    const detail = await getById(row.id);
    const plan = detail || row;
    setPlanForm({
      title: plan.title || '',
      description: plan.description || '',
      careerGoal: plan.careerGoal || '',
      cycleId: plan.cycleId || null,
      goals: (plan.goals && plan.goals.length > 0) ? plan.goals.map((g) => ({
        _key: g.id || newFormKey(),
        title: g.title || '',
        category: g.category || 'technical',
        successCriteria: g.successCriteria || '',
        targetDate: g.targetDate ? g.targetDate.substring(0, 10) : '',
        actions: (g.actions || []).map((a) => ({
          _key: a.id || newFormKey(),
          actionTitle: a.actionTitle || '',
          actionType: a.actionType || 'course',
          targetDate: a.targetDate ? a.targetDate.substring(0, 10) : '',
          notes: a.notes || '',
        })),
      })) : [emptyGoal()],
    });
    setPlanModal({ isOpen: true, plan });
  }, [getById]);

  const closePlanModal = useCallback(() => {
    setPlanModal({ isOpen: false, plan: null });
  }, []);

  const handleSavePlan = useCallback(async () => {
    if (!planForm.title.trim()) return;
    setSavingPlan(true);
    const payload = buildPlanPayload(planForm);
    let ok;
    if (planModal.plan) {
      ok = await update(planModal.plan.id, payload);
    } else {
      ok = await create(payload);
    }
    setSavingPlan(false);
    if (ok) closePlanModal();
  }, [planForm, planModal.plan, create, update, closePlanModal]);

  // Task 35 fix turu — modal içi "Gönder": önce güncel formu kaydeder, ardından submit
  // eder. `planValidation.submittable` false iken buton zaten disabled (bkz. render),
  // bu yalnız son bir güvenlik ağı.
  const handleSaveAndSubmit = useCallback(async () => {
    if (!planForm.title.trim() || !planModal.plan) return;
    const validation = computePlanValidation(planForm.goals);
    if (!validation.submittable) return;
    setSubmittingPlan(true);
    const payload = buildPlanPayload(planForm);
    const ok = await update(planModal.plan.id, payload);
    if (ok) {
      await submit(planModal.plan.id);
      closePlanModal();
    }
    setSubmittingPlan(false);
  }, [planForm, planModal.plan, update, submit, closePlanModal]);

  // Task 35 fix turu (İncelemeci Important #1) — satırdaki hızlı "Gönder" aksiyonu
  // artık körlemesine backend'e gitmiyor: güncel planı çekip AYNI min1/max5 hedef +
  // hedef-başına min1 aksiyon kuralını istemci tarafında uygular. Geçerliyse doğrudan
  // gönderir (mevcut tek-tık deneyimi korunur); değilse editörü (sayaç + hedef-bazlı
  // uyarılar + disabled Gönder ile) açar — kullanıcı hatayı görmeden reddedilen bir
  // istek hiç atılmaz.
  const handleSubmit = useCallback(async (row) => {
    const detail = await getById(row.id);
    const plan = detail || row;
    const validation = computePlanValidation((plan.goals || []).map((g) => ({
      title: g.title || '',
      actions: (g.actions || []).map((a) => ({ actionTitle: a.actionTitle || '' })),
    })));
    if (validation.submittable) {
      await submit(row.id);
    } else {
      await openEditModal(plan);
    }
  }, [getById, submit, openEditModal]);

  // ---- Detail modal (read-only) ----

  const openDetailModal = useCallback(async (row) => {
    setDetailModal({ isOpen: true, plan: row });
    setDetailLoading(true);
    const detail = await getById(row.id);
    if (detail) setDetailModal({ isOpen: true, plan: detail });
    setDetailLoading(false);
  }, [getById]);

  // ---- Progress modal ----

  const openProgressModal = useCallback(async (row) => {
    const detail = await getById(row.id);
    const plan = detail || row;
    const init = {};
    // TD-30 — hedefsiz aksiyonlar da düzenlenebilir olmalı; TEK düzleştirme kuralı
    // (allActionsFlat) kullanılır, aksi halde başlangıç değerleri ile listelenen
    // satırlar ayrışır ve kullanıcı boş bir alan görür.
    allActionsFlat(plan, '').forEach((a) => {
      init[a.id] = { progressPercent: a.progressPercent ?? 0, status: a.status || 'planned' };
    });
    setProgressValues(init);
    setProgressModal({ isOpen: true, plan });
  }, [getById]);

  const handleProgressSave = useCallback(async () => {
    if (!progressModal.plan) return;
    setProgressBusy(true);
    const { plan } = progressModal;
    // TD-30 — kaydetme kümesi de AYNI kuraldan gelir (üç yol da allActionsFlat).
    for (const action of allActionsFlat(plan, '')) {
      const v = progressValues[action.id];
      if (v) {
        await updateActionProgress(plan.id, action.id, {
          progressPercent: Number(v.progressPercent),
          status: v.status || undefined,
        });
      }
    }
    setProgressModal({ isOpen: false, plan: null });
    setProgressBusy(false);
  }, [progressModal, progressValues, updateActionProgress]);

  // ---- Cancel ----

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelConfirm.planId) return;
    setCancelling(true);
    await cancel(cancelConfirm.planId);
    setCancelConfirm({ isOpen: false, planId: null, title: '' });
    setCancelling(false);
  }, [cancelConfirm.planId, cancel]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const items = [
      { type: 'detail', label: t('performance.idp.viewDetail'), onClick: () => openDetailModal(row) },
    ];
    const { status } = row;

    if (EDITABLE_STATUSES.includes(status)) {
      items.push({ type: 'edit', label: t('performance.common.edit'), onClick: () => openEditModal(row) });
    }
    if (SUBMITTABLE_STATUSES.includes(status)) {
      items.push({ type: 'submit', label: t('performance.idp.submit'), onClick: () => handleSubmit(row) });
    }
    if (status === 'active') {
      items.push({ type: 'progress', label: t('performance.idp.progress'), onClick: () => openProgressModal(row) });
    }
    if (CANCELABLE_STATUSES.includes(status)) {
      items.push({ type: 'divider' });
      items.push({ type: 'cancel', label: t('performance.idp.cancel'), onClick: () => setCancelConfirm({ isOpen: true, planId: row.id, title: row.title }) });
    }
    return items;
  }, [t, openDetailModal, openEditModal, handleSubmit, openProgressModal]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'readyForReviewBadge',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row?.readyForReview) return null;
          return <ReadyBadge>{t('performance.idp.readyForReview')}</ReadyBadge>;
        },
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const acts = buildActions(row);
          return acts.length > 0 ? <OptionsMenu actions={acts} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, t, buildActions]);


  const showSubmitButton = Boolean(planModal.plan && SUBMITTABLE_STATUSES.includes(planModal.plan.status));

  return (
    <Container>
      {/* Plan create/edit modal */}
      <Modal $open={planModal.isOpen}>
        {planModal.isOpen && (
          <ModalCard>
            <DialogTitle>
              {planModal.plan ? t('performance.idp.edit') : t('performance.idp.newPlan')}
            </DialogTitle>

            {planModal.plan && planModal.plan.status === 'revision_required' && planModal.plan.managerNote && (
              <RevisionBanner>
                <strong>{t('performance.idp.managerNotes')}:</strong> {planModal.plan.managerNote}
              </RevisionBanner>
            )}

            <FieldGroup>
              <TextInput
                label={t('performance.idp.titleField')}
                value={planForm.title}
                onChange={(e) => setPlanForm((f) => ({ ...f, title: e.target.value }))}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.idp.description')}
                type="textarea"
                rows={3}
                value={planForm.description}
                onChange={(e) => setPlanForm((f) => ({ ...f, description: e.target.value }))}
              />
            </FieldGroup>
            {/* P1 — kariyer hedefi: plan hedeflerinden bağımsız uzun vadeli
                kariyer beklentisi (opsiyonel serbest metin). */}
            <FieldGroup>
              <TextInput
                label={t('performance.idp.careerGoal')}
                type="textarea"
                rows={2}
                value={planForm.careerGoal}
                onChange={(e) => setPlanForm((f) => ({ ...f, careerGoal: e.target.value }))}
              />
            </FieldGroup>
            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.idp.cycle')}
                value={planForm.cycleId}
                onChange={(v) => setPlanForm((f) => ({ ...f, cycleId: v }))}
                onSearch={cycleAsyncProps.onSearch}
                onGetById={cycleAsyncProps.onGetById}
              />
            </FieldGroup>

            {planForm.goals.map((goal, gIdx) => {
              const validActionCount = goal.actions.filter((a) => a.actionTitle.trim()).length;
              const showActionWarning = goal.title.trim() && validActionCount === 0;
              return (
              <GoalCard key={goal._key}>
                {planForm.goals.length > 1 && (
                  <RemoveBtn type="button" onClick={() => removeGoal(gIdx)} title={t('common.delete')}>×</RemoveBtn>
                )}
                <GoalHeader>
                  <CategoryBadge>{t(`performance.idp.category.${goal.category}`)}</CategoryBadge>
                </GoalHeader>
                <FieldGroup>
                  <TextInput
                    label={t('performance.idp.goalTitle')}
                    value={goal.title}
                    onChange={(e) => updateGoalField(gIdx, 'title', e.target.value)}
                    required
                  />
                </FieldGroup>
                <ActionRowGrid>
                  <FieldGroup>
                    <GenericSelect
                      label={t('performance.idp.goalCategory')}
                      value={goal.category}
                      onChange={(v) => updateGoalField(gIdx, 'category', v)}
                      options={categoryOptions}
                      idKey="id"
                      labelKey="name"
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <TextInput
                      label={t('performance.idp.targetDate')}
                      type="date"
                      value={goal.targetDate}
                      onChange={(e) => updateGoalField(gIdx, 'targetDate', e.target.value)}
                    />
                  </FieldGroup>
                </ActionRowGrid>
                <FieldGroup>
                  <TextInput
                    label={t('performance.idp.successCriteria')}
                    type="textarea"
                    rows={2}
                    value={goal.successCriteria}
                    onChange={(e) => updateGoalField(gIdx, 'successCriteria', e.target.value)}
                  />
                </FieldGroup>

                {goal.actions.map((act, aIdx) => (
                  <ActionRow key={act._key}>
                    {goal.actions.length > 1 && (
                      <RemoveBtn type="button" onClick={() => removeAction(gIdx, aIdx)} title={t('common.delete')}>×</RemoveBtn>
                    )}
                    <ActionRowGrid>
                      <FieldGroup>
                        <TextInput
                          label={t('performance.idp.actionTitle')}
                          value={act.actionTitle}
                          onChange={(e) => updateActionField(gIdx, aIdx, 'actionTitle', e.target.value)}
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <GenericSelect
                          label={t('performance.idp.actionTypeLabel')}
                          value={act.actionType}
                          onChange={(v) => updateActionField(gIdx, aIdx, 'actionType', v)}
                          options={actionTypeOptions}
                          idKey="id"
                          labelKey="name"
                        />
                      </FieldGroup>
                    </ActionRowGrid>
                    <ActionRowGrid>
                      <FieldGroup>
                        <TextInput
                          label={t('performance.idp.targetDate')}
                          type="date"
                          value={act.targetDate}
                          onChange={(e) => updateActionField(gIdx, aIdx, 'targetDate', e.target.value)}
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <TextInput
                          label={t('performance.idp.notes')}
                          value={act.notes}
                          onChange={(e) => updateActionField(gIdx, aIdx, 'notes', e.target.value)}
                        />
                      </FieldGroup>
                    </ActionRowGrid>
                  </ActionRow>
                ))}
                {showActionWarning && (
                  <GoalActionWarning>{t('responseErrors.talent.idp.actionRequired')}</GoalActionWarning>
                )}
                <Button variant="outline" size="sm" onClick={() => addAction(gIdx)}>
                  + {t('performance.idp.addAction')}
                </Button>
              </GoalCard>
              );
            })}

            <Button variant="outline" onClick={addGoal} style={{ marginBottom: 16 }}>
              + {t('performance.idp.addGoal')}
            </Button>

            <ValidationRow>
              <GoalCounterText $valid={planValidation.goalCountValid}>
                {t('performance.idp.goalCounter')}: {planValidation.goalCount} / {MAX_GOALS}
              </GoalCounterText>
              {!planValidation.goalCountValid ? (
                <ValidationHint>{t('responseErrors.talent.idp.goalCountRange')}</ValidationHint>
              ) : planValidation.goalsMissingAction > 0 ? (
                <ValidationHint>{t('responseErrors.talent.idp.actionRequired')}</ValidationHint>
              ) : null}
            </ValidationRow>

            <Actions>
              <Button variant="outline" onClick={closePlanModal}>{t('common.cancel')}</Button>
              <Button
                variant={showSubmitButton ? 'outline' : undefined}
                onClick={handleSavePlan}
                disabled={!planForm.title.trim() || savingPlan}
                loading={savingPlan}
              >
                {t('common.save')}
              </Button>
              {showSubmitButton && (
                <Button
                  onClick={handleSaveAndSubmit}
                  disabled={!planForm.title.trim() || !planValidation.submittable || savingPlan || submittingPlan}
                  loading={submittingPlan}
                >
                  {t('performance.idp.submit')}
                </Button>
              )}
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* Detail modal (read-only) */}
      <Modal $open={detailModal.isOpen}>
        {detailModal.isOpen && (
          <ModalCard>
            <DialogTitle>{detailModal.plan?.title || '—'}</DialogTitle>
            {detailLoading ? (
              <div style={{ color: '#6c757d', fontSize: 13 }}>{t('common.loading')}...</div>
            ) : (
              <>
                <IdpFlowBar status={detailModal.plan?.status} />
                {detailModal.plan?.careerGoal && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.careerGoal')}:</DetailLabel>
                    <DetailValue>{detailModal.plan.careerGoal}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>{t('performance.idp.overallProgress')}:</DetailLabel>
                  <DetailValue>
                    {detailModal.plan?.overallProgress != null ? `${detailModal.plan.overallProgress}%` : '-'}
                    {detailModal.plan?.readyForReview && <ReadyBadge>{t('performance.idp.readyForReview')}</ReadyBadge>}
                  </DetailValue>
                </DetailRow>
                {detailModal.plan?.managerSupport && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.managerSupport')}:</DetailLabel>
                    <DetailValue>{detailModal.plan.managerSupport}</DetailValue>
                  </DetailRow>
                )}
                {detailModal.plan?.managerNote && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.managerNotes')}:</DetailLabel>
                    <DetailValue>{detailModal.plan.managerNote}</DetailValue>
                  </DetailRow>
                )}
                {detailModal.plan?.status === 'completed' && (
                  <>
                    <DetailRow>
                      <DetailLabel>{t('performance.idp.employeeReviewNote')}:</DetailLabel>
                      <DetailValue>{detailModal.plan.employeeReviewNote || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('performance.idp.managerReviewNote')}:</DetailLabel>
                      <DetailValue>{detailModal.plan.managerReviewNote || '-'}</DetailValue>
                    </DetailRow>
                  </>
                )}

                {(detailModal.plan?.goals || []).map((g) => (
                  <GoalCard key={g.id}>
                    <GoalHeader>
                      <CategoryBadge>{t(`performance.idp.category.${g.category}`, { defaultValue: g.category })}</CategoryBadge>
                      <strong>{g.title}</strong>
                    </GoalHeader>
                    {g.successCriteria && <DetailValue style={{ display: 'block', marginBottom: 6 }}>{g.successCriteria}</DetailValue>}
                    {g.targetDate && <DetailValue style={{ display: 'block', marginBottom: 6 }}>{t('performance.idp.targetDate')}: {formatDate(g.targetDate, i18n.language)}</DetailValue>}
                    {(g.actions || []).map((a) => (
                      <ActionRow key={a.id}>
                        <div style={{ fontWeight: 600 }}>{a.actionTitle}</div>
                        <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                          {t(`performance.idp.actionType.${a.actionType}`, { defaultValue: a.actionType })}
                          {a.targetDate && ` · ${formatDate(a.targetDate, i18n.language)}`}
                          {' · '}{t(`performance.idp.actionStatus.${a.status}`, { defaultValue: a.status })}
                          {a.progressPercent != null && ` · ${a.progressPercent}%`}
                        </div>
                      </ActionRow>
                    ))}
                  </GoalCard>
                ))}

                {/* TD-30 — hedefe bağlı OLMAYAN aksiyonlar. Ortalamaya zaten giriyorlardı,
                    artık görünüyorlar da. */}
                {(detailModal.plan?.generalActions || []).length > 0 && (
                  <GoalCard>
                    <GoalHeader>
                      <strong>{t('performance.idp.generalActions')}</strong>
                    </GoalHeader>
                    {detailModal.plan.generalActions.map((a) => (
                      <ActionRow key={a.id}>
                        <div style={{ fontWeight: 600 }}>{a.actionTitle}</div>
                        <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                          {t(`performance.idp.actionType.${a.actionType}`, { defaultValue: a.actionType })}
                          {a.targetDate && ` · ${formatDate(a.targetDate, i18n.language)}`}
                          {' · '}{t(`performance.idp.actionStatus.${a.status}`, { defaultValue: a.status })}
                          {a.progressPercent != null && ` · ${a.progressPercent}%`}
                        </div>
                      </ActionRow>
                    ))}
                  </GoalCard>
                )}
              </>
            )}
            <Actions>
              <Button variant="outline" onClick={() => setDetailModal({ isOpen: false, plan: null })}>
                {t('common.close')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* Progress modal */}
      <Modal $open={progressModal.isOpen}>
        {progressModal.isOpen && progressModal.plan && (
          <ModalCard>
            <DialogTitle>{t('performance.idp.progress')} — {progressModal.plan.title}</DialogTitle>
            {(() => {
              let lastGoal = null;
              return allActionsFlat(progressModal.plan, t('performance.idp.generalActions')).map((action) => {
                const showGoalLabel = action.goalTitle !== lastGoal;
                lastGoal = action.goalTitle;
                return (
                  <React.Fragment key={action.id}>
                    {showGoalLabel && <GoalGroupLabel>{action.goalTitle}</GoalGroupLabel>}
                    <ProgressRow>
                      <ProgressLabel>{action.actionTitle}</ProgressLabel>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          label={t('performance.idp.overallProgress')}
                          type="number"
                          value={progressValues[action.id]?.progressPercent ?? 0}
                          onChange={(e) => setProgressValues((prev) => ({
                            ...prev,
                            [action.id]: { ...prev[action.id], progressPercent: e.target.value },
                          }))}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <GenericSelect
                          label={t('performance.idp.fields.status')}
                          value={progressValues[action.id]?.status || ''}
                          onChange={(v) => setProgressValues((prev) => ({
                            ...prev,
                            [action.id]: { ...prev[action.id], status: v },
                          }))}
                          options={actionStatusOptions}
                          idKey="id"
                          labelKey="name"
                        />
                      </div>
                    </ProgressRow>
                  </React.Fragment>
                );
              });
            })()}
            <Actions>
              <Button variant="outline" onClick={() => setProgressModal({ isOpen: false, plan: null })}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleProgressSave} disabled={progressBusy} loading={progressBusy}>
                {t('common.save')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('performance.idp.cancel')}
        message={t('performance.common.deleteMessage', { name: cancelConfirm.title })}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, planId: null, title: '' })}
        loading={cancelling}
      />

      {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
      {loadFailed && <LoadErrorBanner />}

      {useMemo(() => (
        <DataTable
          title={t('performance.idp.myTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          onNewButtonClick={openCreateModal}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        openCreateModal, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
